import React from "react";
import styles from "./FieldStyler.module.css";
import toolbarStyles from "../../CygnusFormBuilderToolbar.module.css";
import { FieldStylerProps } from "./FieldStyler.utils";
import { Row } from "react-bootstrap";
import Select from "react-select";

/**
 * `FieldStyler` is a reusable margin editor component that allows users
 * to input top, right, bottom, and left margins individually for a form field.
 *
 * This component is typically used in form builders to control spacing around input components.
 *
 * @component
 * @example
 * <FieldStyler
 *   margins={{ top: 10, right: 20, bottom: 10, left: 5 }}
 *   updateMargin={(value, side) => handleMarginChange(value, side)}
 * />
 */
export const FieldStyler: React.FC<FieldStylerProps> = ({
  isFinal,
  selectedField,
  updateField,
}) => {
  /**
   * Renders a single numeric margin input field for a given side.
   *
   * @param label - Label text shown above the input.
   * @param id - Identifier used as both input ID and side key.
   * @param onChange - Function to call when value changes.
   * @param value - Current value of the margin for this side.
   */
  const renderMarginInput = (label: string, id: string, value?: number) => (
    <div className={styles.customNumberInputWrapper}>
      <label style={{ fontWeight: 500, fontSize: 8 }}>{label}</label>
      <div className={toolbarStyles.customNumberInput}>
        <button
          className={toolbarStyles.decrement}
          onClick={() =>
            value &&
            value > 0 &&
            updateField({
              margins: { ...selectedField.margins, [id]: value - 1 },
            })
          }
        >
          −
        </button>
        <input
          type="number"
          className={styles.marginInput}
          id={id}
          aria-describedby={id}
          value={value}
          onChange={(e) =>
            updateField({
              margins: {
                ...selectedField.margins,
                [id]: Number(e.target.value),
              },
            })
          }
        />
        <button
          className={toolbarStyles.increment}
          onClick={() =>
            value &&
            updateField({
              margins: { ...selectedField.margins, [id]: value + 1 },
            })
          }
        >
          +
        </button>
      </div>
    </div>
  );

  const widthOptions = Array.from({ length: 10 }, (_, i) => {
    const percent = (i + 1) * 10;
    return {
      value: percent,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "60px",
              height: "8px",
              background: "#e0e0e0",
              position: "relative",
              borderRadius: "4px",
            }}
          >
            <div
              style={{
                width: `${percent}%`,
                height: "100%",
                background: "var(--theme1_primary)",
                borderRadius: "4px",
              }}
            />
          </div>
          <span>{percent}%</span>
        </div>
      ),
    };
  });

  return (
    <>
      {/* Field Width */}
      <div className={toolbarStyles.formGroup}>
        <label>Field Width (%)</label>
        <Select
          options={widthOptions}
          value={widthOptions.find((opt) => opt.value === selectedField.width)}
          onChange={(option) => {
            if (option) {
              updateField({ width: option.value });
            }
          }}
          placeholder="Select width"
          isDisabled={isFinal}
          classNamePrefix="react-select"
          className="custom-select"
        />
      </div>
      {/* Field Margins */}
      <Row className={`no-gutter ${styles.fieldMarginWrapper}`}>
        {renderMarginInput("Top", "top", selectedField?.margins?.top)}
        {renderMarginInput("Right", "right", selectedField?.margins?.right)}
        {renderMarginInput("Bottom", "bottom", selectedField?.margins?.bottom)}
        {renderMarginInput("Left", "left", selectedField?.margins?.left)}
      </Row>
    </>
  );
};

export default FieldStyler;
